import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useOnboarding } from "@/context/OnboardingContext";
import WelcomeStep from "@/components/onboarding/WelcomeStep";
import TutorialStep from "@/components/onboarding/TutorialStep";
import MissionStep from "@/components/onboarding/MissionStep";

export default function OnboardingFlow() {
  const onboarding = useOnboarding();
  const loc = useLocation();
  const [step, setStep] = useState(0); // 0 welcome, 1 tutorial, 2 mission
  const [exiting, setExiting] = useState(false);

  // Skip onboarding on admin, landing, and auth routes
  const skipOnRoute = loc.pathname.startsWith("/admin")
    || loc.pathname.startsWith("/landing")
    || loc.pathname.startsWith("/login")
    || loc.pathname.startsWith("/register")
    || loc.pathname.startsWith("/forgot-password");

  useEffect(() => {
    if (onboarding?.open && !skipOnRoute) {
      setStep(0);
      setExiting(false);
    }
  }, [onboarding?.open, skipOnRoute]);

  if (!onboarding?.open || skipOnRoute) return null;

  const next = () => setStep((s) => Math.min(2, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const finish = () => {
    setExiting(true);
    setTimeout(() => onboarding.finish(), 380);
  };

  return (
    <div
      data-testid="onboarding"
      className={`fixed inset-0 z-[90] flex items-center justify-center px-4 py-6 bg-cream/95 backdrop-blur-md transition-opacity duration-500 ${
        exiting ? "opacity-0" : "opacity-100"
      }`}
    >
      <div className="absolute top-5 right-5 flex items-center gap-3">
        <span data-testid="onboarding-step-indicator" className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-mid">
          paso {String(step + 1).padStart(2, "0")} / 03
        </span>
        <button
          onClick={finish}
          data-testid="onboarding-skip"
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid hover:text-coral transition link-underline"
        >
          Saltar
        </button>
      </div>

      <div className="w-full max-w-4xl">
        {step === 0 && <WelcomeStep onNext={next} />}
        {step === 1 && <TutorialStep onNext={next} onPrev={prev} />}
        {step === 2 && <MissionStep onFinish={finish} onPrev={prev} />}
      </div>

      {/* Step dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {[0, 1, 2].map((i) => (
          <button
            key={i}
            onClick={() => setStep(i)}
            data-testid={`onboarding-dot-${i}`}
            aria-label={`Ir al paso ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${
              i === step ? "w-8 bg-coral" : "w-1.5 bg-ink/20 hover:bg-ink/40"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
